import React from "react";
import { useTranslation } from "react-i18next";
import { Country, State } from "country-state-city";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import numberToWord from "../../components/common/utlity/invoiceword";
import { AWS_URL } from "../../services/http.service";
import { getCurrencySymbol } from "../../components/common/utlity/options";
import { momentDateFormat } from "../../components/common/utlity/utility";
import { useSelector } from "react-redux";

const formatCurrency = (val, currency) => {
  let num = 0;

  if (typeof val === "number") {
    num = val;
  } else if (typeof val === "string") {
    num = parseFloat(val);
    if (isNaN(num)) num = 0;
  }

  return `${getCurrencySymbol(currency)} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (str) => {
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date)) return str;
  return date.toLocaleDateString();
};

const GreenDoubleDevine = ({
  item,
  containerRef,
  businessName,
  phone,
  darkMode = false,
  bussiness,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);

  if (!item) return <div>No invoice data available</div>;

  const country = Country.getCountryByCode(bussiness?.country);
  const state = State.getStateByCodeAndCountry(
    bussiness?.state,
    bussiness?.country
  );
  const itemCountry = Country.getCountryByCode(item?.country);
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);

  const isIntraState = bussiness?.state === item?.state;

  const calculateItemAmount = (item) => {
    const quantity = Number(item.quantity) || 0;
    const salePrice = Number(item.salePrice) || 0;
    const discountValue = parseFloat(item.discount || 0);
    const discountUnit = item.discountUnit || "%";

    const itemTotal = quantity * salePrice;

    let discountAmount = 0;
    if (
      discountUnit === "₹" ||
      discountUnit === "INR" ||
      discountUnit === "amount"
    ) {
      discountAmount = discountValue;
    } else {
      discountAmount = (itemTotal * discountValue) / 100;
    }

    return itemTotal - discountAmount;
  };

  const itemsToUse = item?.invoiceId?.startsWith("RT")
    ? item?.returnitems
    : item?.items;
  // console.log("itemsToUse", itemsToUse);
  const totalQuantity =
    itemsToUse?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;

  const totalAmount =
    itemsToUse?.reduce(
      (sum, i) => sum + (Number(i.quantity) * Number(i.salePrice) || 0),
      0
    ) || 0;

  const paidAmount =
    (parseFloat(item.transactionAmount) || 0) -
    (parseFloat(item.remainingTransactionAmount) || 0);
  const remainingBalance = item.remainingTransactionAmount || 0;

  const totalItemDiscount =
    itemsToUse?.reduce((sum, i) => {
      const discountValue = parseFloat(i.discount || 0);
      const discountUnit = i.discountUnit || "%";
      const quantity = parseFloat(i.quantity || 0);
      const salePrice = parseFloat(i.salePrice || 0);
      const itemTotal = quantity * salePrice;

      let discountAmount = 0;
      if (
        discountUnit === "₹" ||
        discountUnit === "INR" ||
        discountUnit === "amount"
      ) {
        discountAmount = discountValue;
      } else {
        discountAmount = (itemTotal * discountValue) / 100;
      }

      return sum + discountAmount;
    }, 0) || 0;

  const totalDiscount = totalItemDiscount;

  const isNonGSTInvoice = item?.showGST === false;
  const isReturnInvoice = item?.invoiceId?.startsWith("RT");
  const isEstimate = item?.saleType === "estimate";
  const isAdvancePayment = item?.saleType === "advancePayment";

  const hsnSummary = itemsToUse?.reduce((acc, item) => {
    const hsn = item.hsnNumber || item.hsn || "N/A";
    const taxableValue = Number(item.quantity) * Number(item.salePrice) || 0;

    const isNoGSTItem =
      isNonGSTInvoice ||
      (isIntraState &&
        (!item.cgst || parseFloat(item.cgst) === 0) &&
        (!item.sgst_utgst || parseFloat(item.sgst_utgst) === 0)) ||
      (!isIntraState && (!item.igst || parseFloat(item.igst) === 0));

    let totalGstRate = 0;
    let taxAmount = 0;

    if (!isNoGSTItem) {
      if (isIntraState) {
        totalGstRate =
          parseFloat(item.cgst || 0) + parseFloat(item.sgst_utgst || 0);
      } else {
        totalGstRate = parseFloat(item.igst || 0);
      }
      taxAmount = (taxableValue * totalGstRate) / 100;
    }

    if (!acc[hsn]) {
      acc[hsn] = {
        hsn: hsn,
        taxableValue: 0,
        cgstRate: isIntraState && !isNoGSTItem ? parseFloat(item.cgst || 0) : 0,
        sgstRate:
          isIntraState && !isNoGSTItem ? parseFloat(item.sgst_utgst || 0) : 0,
        igstRate:
          !isIntraState && !isNoGSTItem ? parseFloat(item.igst || 0) : 0,
        cessRate: parseFloat(item.cess || 0),
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        cessAmount: 0,
        totalTax: 0,
        totalGstRate: 0,
        isNoGST: isNoGSTItem,
      };
    }

    acc[hsn].taxableValue += taxableValue;
    acc[hsn].totalTax += taxAmount;
    acc[hsn].totalGstRate = totalGstRate;

    if (!isNoGSTItem) {
      if (isIntraState) {
        acc[hsn].cgstAmount +=
          (taxableValue * parseFloat(item.cgst || 0)) / 100;
        acc[hsn].sgstAmount +=
          (taxableValue * parseFloat(item.sgst_utgst || 0)) / 100;
      } else {
        acc[hsn].igstAmount +=
          (taxableValue * parseFloat(item.igst || 0)) / 100;
      }
    }

    acc[hsn].cessAmount += (taxableValue * parseFloat(item.cess || 0)) / 100;

    return acc;
  }, {});

  const hsnSummaryArray = Object.values(hsnSummary || {});
  const totalTaxAmount = hsnSummaryArray.reduce(
    (sum, hsn) => sum + hsn.totalTax + hsn.cessAmount,
    0
  );

  const grandTotalBeforeDiscount = totalAmount + totalTaxAmount;
  const grandTotal = grandTotalBeforeDiscount - totalDiscount;
  const amountInwords = numberToWord(grandTotal);

  let currentAmount = grandTotal;
  const totalAdditionalDiscount = Array.isArray(item.additionalDiscount)
    ? item.additionalDiscount.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let discountAmount = 0;
        if (d.type === "discount") {
          discountAmount = (currentAmount * amount) / 100;
        } else {
          discountAmount = amount;
        }
        currentAmount -= discountAmount;
        return sum + discountAmount;
      }, 0)
    : 0;

  const totalAdditionalFee = Array.isArray(item.additionalFee)
    ? item.additionalFee.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let feeAmount = 0;
        if (d.type === "discount") {
          feeAmount = (currentAmount * amount) / 100;
        } else {
          feeAmount = amount;
        }
        currentAmount += feeAmount;
        return sum + feeAmount;
      }, 0)
    : 0;

  const isEntireInvoiceNoGST =
    isNonGSTInvoice || hsnSummaryArray.every((hsn) => hsn.isNoGST);

  return (
    <div
      ref={containerRef}
      className={`max-w-5xl mx-auto p-6 rounded-lg shadow-sm ${
        darkMode ? "dark:bg-[#020617] text-white" : "bg-white"
      }`}
        style={{
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <div className='flex justify-between items-start mb-[1.5rem] '>
        <div>
          {isReturnInvoice ? (
            <h2
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Return {t("SALE.INVOICE")}/Debit Note
            </h2>
          ) : isEstimate ? (
            <h2
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Estimate Invoice
            </h2>
          ) : (
            <h2
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            ></h2>
          )}
        </div>

        <div className='text-right'>
          {isNonGSTInvoice && !isEstimate && (
            <div
              className={`text-xs ${
                darkMode ? "text-red-400" : "text-red-600"
              } font-semibold mb-[1.5rem]`}
            >
              This is a Non-GST Invoice; cannot use for GST Returns
            </div>
          )}

          {isAdvancePayment && (
            <div
              className={`text-xs text-right ${
                darkMode ? "text-blue-400" : "text-blue-600"
              } font-bold mb-2`}
            >
              This is a Proforma Invoice
            </div>
          )}

          {!isReturnInvoice && (
            <span
              className={`font-bold text-sm ${
                isNonGSTInvoice
                  ? darkMode
                    ? "text-yellow-400"
                    : "text-yellow-600"
                  : darkMode
                  ? "text-green-400"
                  : "text-green-600"
              }`}
            ></span>
          )}
        </div>
      </div>

      <div className='flex items-stretch h-28 -mx-6 -mt-6 mb-6 overflow-hidden'>
        <div className='relative flex-1  bg-[#0B5844] flex items-center px-6'>
          <div className='flex items-center'>
            <div className='w-12 h-12 mr-3 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 shadow'>
              <img
                src={
                  bussiness?.businesslogo
                    ? AWS_URL + bussiness.businesslogo
                    : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
                }
                alt='Business Logo'
                className='object-contain w-full h-full'
                // crossOrigin='anonymous'
              />
            </div>

            <div className='flex flex-col'>
              <h1 className='text-white text-2xl font-normal'>
                {businessName || "Business Name"}
              </h1>
              {!isNonGSTInvoice && (
                <p className='text-white text-xs'>
                  GSTIN: {bussiness.gstNumber || "N/A"}
                </p>
              )}
              <p className='text-white text-xs'>
                Email: {bussiness?.email || "N/A"}
              </p>
              <p className='text-xs text-white'>
                Address:
                {`${bussiness.firstLine},${state?.name},${country?.name}` ||
                  "N/A"}
              </p>
            </div>
          </div>

          <div className='absolute -right-10 top-0 h-full w-20'>
            <div className='relative h-full w-full'>
              <div className='absolute top-0 right-0 h-1/2 w-full  bg-[#f9f7f7] rounded-bl-full'></div>
              <div className='absolute bottom-0 left-0 h-1/2 w-full  bg-[#0B5844] rounded-tr-full'></div>
            </div>
          </div>
        </div>

        <div
          className={`relative flex-1 flex items-center pr-12 ${
            darkMode ? " bg-[#f9f7f7]" : " bg-[#f9f7f7]"
          }`}
          style={{ height: "50%" }}
        >
          <div className='flex items-center ml-auto'>
            <svg
              className={`w-5 h-5 mr-2 ${
                darkMode ? "text-black" : "text-black"
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
              />
            </svg>
            <span
              className={`text-base font-bold ${
                darkMode ? "text-black" : "text-black"
              }`}
            >
              {phone || "0000000000"}
            </span>
          </div>
        </div>
      </div>

      {!isNonGSTInvoice && (
        <div
          className={`mb-4 p-3 rounded-lg ${
            darkMode ? "bg-[#f9f7f7] text-black" : "bg-[#f9f7f7] text-black"
          }`}
        >
          <div className='grid grid-cols-2 gap-4 text-sm'>
            <div>
              <span className='font-semibold'>State Name:</span>{" "}
              {state?.name || "N/A"}, Code: {state?.isoCode || "N/A"}
            </div>
          </div>
        </div>
      )}

      <div className='mb-6'>
        <h2
          className={`text-xl font-normal ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          {t("SALE.INVOICE-BILL")}
        </h2>

        <div className='flex justify-between'>
          <div>
            <h2
              className={`text-l font-semibold mb-2 flex items-center gap-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {t("NEWINVOICE.BILLINGTO")}:{" "}
              <span className={darkMode ? "text-white" : "text-gray-800"}>
                {item.customerName || "-"}
              </span>
            </h2>

            <p className={darkMode ? "text-white" : "text-gray-600"}>
              <span className='font-medium'>{t("NEWINVOICE.CONTACT_NO")}:</span>{" "}
              {item.mobile || "-"}
            </p>
            <p
              className={`${
                darkMode ? "text-gray-300" : "text-gray-600"
              } text-xs`}
            >
              {`${item.address} ,${itemState?.name},${itemCountry?.name}` ||
                "N/A"}
            </p>
            <p
              className={`text-xs ${
                darkMode ? "text-amber-400" : "text-amber-600"
              } font-semibold mt-1`}
            >
              {isEntireInvoiceNoGST
                ? "No-GST Supply"
                : isIntraState
                ? "Intra-State Supply"
                : "Inter-State Supply"}
            </p>
          </div>
          <div className='text-right'>
            <p
              className={
                darkMode
                  ? "text-white font-semibold"
                  : "text-gray-700 font-semibold"
              }
            >
              {t("NEWINVOICE.NO")}: {item.invoiceId || "-"}
            </p>
            <p className={darkMode ? "text-white" : "text-gray-700"}>
              {t("NEWINVOICE.DATE")}: {formatDate(item.saleDate)}
            </p>

            {item?.ewayBillDate && (
              <div
                className={`text-xs ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Eway Bill Date: {item.ewayBillDate}
              </div>
            )}

            {item?.ewayBillNo && (
              <div
                className={`text-xs ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Eway Bill No: {item.ewayBillNo}
              </div>
            )}

            {item?.validUpto && (
              <div
                className={`text-xs ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Valid Upto: {item.validUpto}
              </div>
            )}

            {item?.committedDateToPay && (
              <div
                className={`text-xs font-bold ${
                  darkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                {t("COMMON.PAYMENT_DUE_DATE")}:{" "}
                {formatDate(item.committedDateToPay)}
              </div>
            )}
          </div>
        </div>

        {item?.deliveryAddress && (
          <div className='mt-4'>
            <h3
              className={`text-sm font-semibold ${
                darkMode ? "text-white" : "text-gray-700"
              }`}
            >
              {t("COMMON.SHIPPING-ADDRESS")}
            </h3>
            <p className={darkMode ? "text-gray-300" : "text-gray-600"}>
              {item.deliveryAddress}
            </p>
          </div>
        )}
      </div>

      <div className='mb-6 overflow-x-auto'>
        <table className='min-w-full border-collapse border border-gray-300 rounded-lg text-sm'>
          <thead>
            <tr className='text-black'>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-left font-medium rounded-tl-lg'>
              {t("INVOICE.SL_NO")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-left font-medium'>
                 {t("INVOICE.DESCRIPTION_OF_GOODS")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-left font-medium'>
                {t("INVOICE.HSN_SAC")}
              </th>
              {!isEntireInvoiceNoGST && (
                <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                   {t("INVOICE.GST_RATE")}
                </th>
              )}
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                {t("INVOICE.QUANTITY")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                {t("INVOICE.RATE")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                 {t("INVOICE.UNIT")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                 {t("INVOICE.DISCOUNT")}
              </th>
              <th className='py-2 px-3  bg-[#f9f7f7] border border-gray-300 text-right font-medium rounded-tr-lg'>
                 {t("INVOICE.AMOUNT")}
              </th>
            </tr>
          </thead>
          <tbody>
            {itemsToUse?.map((itm, idx) => {
              const itemAmount =
                Number(itm.quantity) * Number(itm.salePrice) || 0;
              const discountRate = parseFloat(itm.discount || 0);
              const discountUnit = itm.discountUnit || "%";
              let discountAmount = 0;
              if (
                discountUnit === "₹" ||
                discountUnit === "INR" ||
                discountUnit === "amount"
              ) {
                discountAmount = discountRate;
              } else {
                discountAmount = (itemAmount * discountRate) / 100;
              }
              const amountAfterDiscount = itemAmount - discountAmount;
              const isNoGSTItem =
                isNonGSTInvoice ||
                (isIntraState &&
                  (!itm.cgst || parseFloat(itm.cgst) === 0) &&
                  (!itm.sgst_utgst || parseFloat(itm.sgst_utgst) === 0)) ||
                (!isIntraState && (!itm.igst || parseFloat(itm.igst) === 0));
              const gstRate = isIntraState
                ? parseFloat(itm.cgst || 0) + parseFloat(itm.sgst_utgst || 0)
                : parseFloat(itm.igst || 0);
              const gstAmount = !isNoGSTItem ? (itemAmount * gstRate) / 100 : 0;

              return (
                <tr
                  key={idx}
                  className={`${
                    idx % 2 === 0
                      ? darkMode
                        ? "dark:bg-[#170f2a]"
                        : "bg-white"
                      : darkMode
                      ? "dark:bg-[#170f2a]"
                      : "bg-gray-50"
                  }`}
                >
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {idx + 1}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 font-semibold ${
                      darkMode ? "text-white" : "text-gray-600"
                    }`}
                  >
                    {itm.productName || itm.description || "-"}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {truncateCommaSeparatedString(itm.hsnNumber) ||
                      itm.hsn ||
                      "N/A"}
                  </td>
                  {!isEntireInvoiceNoGST && (
                    <td
                      className={`py-2 px-3 border border-gray-300 text-center ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {!isNoGSTItem ? (
                        <>
                          <div className='text-xss'>({gstRate}%)</div>
                          <div>
                            {formatCurrency(gstAmount, bussiness?.currency)}
                          </div>
                          {parseFloat(itm.cess || 0) > 0 && (
                            <div className='text-xss text-amber-600'>
                              Cess: {itm.cess}% (
                              {formatCurrency(
                                (itemAmount * parseFloat(itm.cess || 0)) / 100,
                                bussiness?.currency
                              )}
                              )
                            </div>
                          )}
                        </>
                      ) : (
                        <div className='text-xss text-gray-500'>No GST</div>
                      )}
                    </td>
                  )}
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {itm.quantity || 0}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-right ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {formatCurrency(itm.salePrice, bussiness?.currency)}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {itm.unitSize || "Pcs"}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {discountRate > 0 ? (
                      <>
                        <div>
                          {discountUnit === "₹" ||
                          discountUnit === "INR" ||
                          discountUnit === "amount"
                            ? formatCurrency(discountRate, bussiness?.currency)
                            : `${discountRate}%`}
                        </div>
                        <div className='text-red-600 text-xs'>
                          -{formatCurrency(discountAmount, bussiness?.currency)}
                        </div>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-right ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {formatCurrency(itemAmount, bussiness?.currency)}
                  </td>
                </tr>
              );
            })}
            <tr
              className={`${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } font-semibold`}
            >
              <td
                colSpan={isEntireInvoiceNoGST ? "7" : "8"}
                className={`py-2 px-3 border border-gray-300 text-right ${
                  darkMode ? "text-white" : "text-gray-700"
                }`}
              >
                Total Amount
              </td>
              <td
                className={`py-2 px-3 border border-gray-300 text-right ${
                  darkMode ? "text-white" : "text-gray-700"
                }`}
              >
                {formatCurrency(totalAmount, bussiness?.currency)}
              </td>
            </tr>
            {!isEntireInvoiceNoGST &&
              (() => {
                const cgstAmount = isIntraState
                  ? hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.cgstAmount,
                      0
                    )
                  : 0;
                const sgstAmount = isIntraState
                  ? hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.sgstAmount,
                      0
                    )
                  : 0;
                const igstAmount = !isIntraState
                  ? hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.igstAmount,
                      0
                    )
                  : 0;
                const cessAmount = hsnSummaryArray.reduce(
                  (sum, hsn) => sum + hsn.cessAmount,
                  0
                );

                return (
                  <>
                    {isIntraState && cgstAmount > 0 && (
                      <tr
                        className={`${
                          darkMode ? "bg-gray-800" : "bg-gray-100"
                        } font-semibold`}
                      >
                        <td
                          colSpan='8'
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                             {t("INVOICE.TOTAL_CGST")}
                        </td>
                        <td
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(cgstAmount, bussiness?.currency)}
                        </td>
                      </tr>
                    )}
                    {isIntraState && sgstAmount > 0 && (
                      <tr
                        className={`${
                          darkMode ? "bg-gray-800" : "bg-gray-100"
                        } font-semibold`}
                      >
                        <td
                          colSpan='8'
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {t("INVOICE.TOTAL_SGST")}
                        </td>
                        <td
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(sgstAmount, bussiness?.currency)}
                        </td>
                      </tr>
                    )}
                    {!isIntraState && igstAmount > 0 && (
                      <tr
                        className={`${
                          darkMode ? "bg-gray-800" : "bg-gray-100"
                        } font-semibold`}
                      >
                        <td
                          colSpan='8'
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {t("INVOICE.TOTAL_IGST")}
                        </td>
                        <td
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(igstAmount, bussiness?.currency)}
                        </td>
                      </tr>
                    )}
                    {cessAmount > 0 && (
                      <tr
                        className={`${
                          darkMode ? "bg-gray-800" : "bg-gray-100"
                        } font-semibold`}
                      >
                        <td
                          colSpan='8'
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                            {t("INVOICE.CESS")}
                        </td>
                        <td
                          className={`py-2 px-3 border border-gray-300 text-right ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(cessAmount, bussiness?.currency)}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })()}
            <tr
              className={`${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } font-semibold`}
            >
              <td
                colSpan={isEntireInvoiceNoGST ? "7" : "8"}
                className={`py-2 px-3 border border-gray-300 text-right ${
                  darkMode ? "text-white" : "text-green-800"
                }`}
              >
               {t("INVOICE.AMOUNT_BEFORE_DISCOUNT")}
              </td>
              <td
                className={`py-2 px-3 border border-gray-300 text-right ${
                  darkMode ? "text-white" : "text-green-800"
                }`}
              >
                {formatCurrency(grandTotalBeforeDiscount, bussiness?.currency)}
              </td>
            </tr>
            {totalDiscount > 0 && (
              <tr
                className={`${
                  darkMode ? "bg-gray-800" : "bg-gray-100"
                } font-semibold`}
              >
                <td
                  colSpan={isEntireInvoiceNoGST ? "7" : "8"}
                  className={`py-2 px-3 border border-gray-300 text-right ${
                    darkMode ? "text-white" : "text-gray-700"
                  }`}
                >
                     {t("INVOICE.TOTAL_DISCOUNT")}
                </td>
                <td
                  className={`py-2 px-3 border border-gray-300 text-right text-red-600 ${
                    darkMode ? "text-red-400" : "text-red-600"
                  }`}
                >
                  -{formatCurrency(totalDiscount, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr
              className={`font-bold ${
                darkMode ? "bg-[#0B5844] text-white" : "bg-[#0B5844] text-white"
              }`}
            >
              <td
                colSpan={isEntireInvoiceNoGST ? "7" : "8"}
                className='py-2 px-3 border border-gray-300 text-right rounded-bl-lg'
              >
              {t("INVOICE.GRAND_TOTAL")}
              </td>
              <td className='py-2 px-3 border border-gray-300 text-right rounded-br-lg'>
                {formatCurrency(grandTotal, bussiness?.currency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!isEntireInvoiceNoGST && (
        <div className='mb-6 overflow-x-auto'>
          <h3
            className={`text-lg font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-700"
            }`}
          >
              {t("INVOICE.HSN_TAX_SUMMARY")}
          </h3>
          <table className='min-w-full border-collapse border border-gray-300 rounded-lg text-sm'>
            <thead>
              <tr className='text-black'>
                <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-left font-medium rounded-tl-lg'>
               {t("INVOICE.HSN_SAC")}
                </th>
                <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-right font-medium'>
                       {t("INVOICE.TAXABLE_VALUE")}
                </th>
                {isIntraState ? (
                  <>
                    <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                            {t("INVOICE.CGST")}
                    </th>
                    <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                     {t("INVOICE.SGST_UTGST")}
                    </th>
                  </>
                ) : (
                  <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                    {t("INVOICE.IGST")}
                  </th>
                )}
                <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-center font-medium'>
                   {t("INVOICE.CESS")}
                </th>
                <th className='py-2 px-3 bg-[#f9f7f7] border border-gray-300 text-center font-medium rounded-tr-lg'>
                    {t("INVOICE.TOTAL_TAX_AMOUNT")}
                </th>
              </tr>
            </thead>
            <tbody>
              {hsnSummaryArray.map((hsn, idx) => (
                <tr
                  key={idx}
                  className={
                    idx % 2 === 0
                      ? darkMode
                        ? "dark:bg-green-600"
                        : "bg-white"
                      : darkMode
                      ? "dark:bg-green-500"
                      : "bg-gray-50"
                  }
                >
                  <td
                    className={`py-2 px-3 border border-gray-300 ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {truncateCommaSeparatedString(hsn.hsn) || "N/A"}
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-right ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {formatCurrency(hsn.taxableValue, bussiness?.currency)}
                  </td>
                  {isIntraState ? (
                    <>
                      <td
                        className={`py-2 px-3 border border-gray-300 text-center ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {hsn.isNoGST ? (
                          <div className='text-gray-500'>N/A</div>
                        ) : (
                          <>
                            <div>{hsn.cgstRate}%</div>
                            <div>
                              {formatCurrency(
                                hsn.cgstAmount,
                                bussiness?.currency
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      <td
                        className={`py-2 px-3 border border-gray-300 text-center ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {hsn.isNoGST ? (
                          <div className='text-gray-500'>N/A</div>
                        ) : (
                          <>
                            <div>{hsn.sgstRate}%</div>
                            <div>
                              {formatCurrency(
                                hsn.sgstAmount,
                                bussiness?.currency
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </>
                  ) : (
                    <td
                      className={`py-2 px-3 border border-gray-300 text-center ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {hsn.isNoGST ? (
                        <div className='text-gray-500'>N/A</div>
                      ) : (
                        <>
                          <div>{hsn.igstRate}%</div>
                          <div>
                            {formatCurrency(
                              hsn.igstAmount,
                              bussiness?.currency
                            )}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <div>{hsn.cessRate}%</div>
                    <div>
                      {formatCurrency(hsn.cessAmount, bussiness?.currency)}
                    </div>
                  </td>
                  <td
                    className={`py-2 px-3 border border-gray-300 text-center ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <div>
                      {hsn.isNoGST
                        ? hsn.cessRate
                        : hsn.totalGstRate + hsn.cessRate}
                      %
                    </div>
                    <div>
                      {formatCurrency(
                        hsn.totalTax + hsn.cessAmount,
                        bussiness?.currency
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              <tr
                className={`font-bold ${
                  darkMode
                    ? "bg-[#0B5844] text-white"
                    : "bg-[#0B5844] text-white"
                }`}
              >
                <td className='py-2 px-3 border border-gray-300 rounded-bl-lg'>
                  Total
                </td>
                <td className='py-2 px-3 border border-gray-300 text-right'>
                  {formatCurrency(totalAmount, bussiness?.currency)}
                </td>
                {isIntraState ? (
                  <>
                    <td className='py-2 px-3 border border-gray-300 text-center'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.cgstAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                    <td className='py-2 px-3 border border-gray-300 text-center'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.sgstAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                  </>
                ) : (
                  <td className='py-2 px-3 border border-gray-300 text-center'>
                    {formatCurrency(
                      hsnSummaryArray.reduce(
                        (sum, hsn) => sum + hsn.igstAmount,
                        0
                      ),
                      bussiness?.currency
                    )}
                  </td>
                )}
                <td className='py-2 px-3 border border-gray-300 text-center'>
                  {formatCurrency(
                    hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.cessAmount,
                      0
                    ),
                    bussiness?.currency
                  )}
                </td>
                <td className='py-2 px-3 border border-gray-300 text-center rounded-br-lg'>
                  {formatCurrency(totalTaxAmount, bussiness?.currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className='grid grid-cols-2 gap-6 mb-6'>
        <div>
          <div className='mb-4'>
            <h3
              className={`text-sm font-semibold ${
                darkMode ? "text-white" : "text-gray-700"
              }`}
            >
            {t("INVOICE.AMOUNT_CHARGEABLE_IN_WORDS")}
            </h3>
            <p
              className={
                darkMode
                  ? "text-xs text-gray-400 italic"
                  : "text-gray-600 italic text-xs"
              }
            >
              {numberToWord(grandTotal)} Rupees only
            </p>
          </div>
          {!isEntireInvoiceNoGST && (
            <div className='mb-4'>
              <h3
                className={`text-sm font-semibold ${
                  darkMode ? "text-white" : "text-gray-700"
                }`}
              >
                {t("INVOICE.TAX_AMOUNT_IN_WORDS")}
              </h3>
              <p
                className={
                  darkMode
                    ? "text-gray-400 italic text-xs"
                    : "text-gray-600 italic text-xs"
                }
              >
                {numberToWord(totalTaxAmount)} Rupees only
              </p>
            </div>
          )}
          <div className='mt-4 pt-4 border-t border-gray-300'>
            <h3
              className={`text-sm font-semibold mb-2 ${
                darkMode ? "text-white" : "text-gray-700"
              }`}
            >
                {t("INVOICE.TERMS_DECLARATION")}
            </h3>
            <div
              className={`text-xs space-y-2 ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              <p>
                           <strong>{t("INVOICE.DECLARATION")}:</strong>
              </p>
              <ul className='list-disc list-inside space-y-1'>
                <li>
                         {t("INVOICE.DECLARATION_ACTUAL_PRICE")}
                </li>
                <li>
                      {t("INVOICE.DECLARATION_PARTICULARS_TRUE")}
                </li>
              </ul>
              <p>
                 <strong>{t("INVOICE.TERMS")}:</strong>
              </p>
              <ul className='list-disc list-inside space-y-1'>
        <li>{t("INVOICE.TERMS_NO_RETURN")}</li>
                <li>{t("INVOICE.TERMS_PAYMENT_DUE")}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className='col-span-1'>
          <div className='mb-6 max-w-xs ml-auto'>
            <table className='w-full border-collapse border border-gray-300 text-sm'>
              <tbody>
                <tr
                  className={` bg-[#f9f7f7] font-bold ${
                    darkMode ? "bg-gray-800 text-black" : "text-gray-800"
                  }`}
                >
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 text-black-300"
                        : "border-gray-300 text-gray-600"
                    } font-medium`}
                  >
                    {t("NEWINVOICE.SUB_TOTAL")}
                  </td>
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 black-gray-300"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    {formatCurrency(grandTotal, bussiness?.currency)}
                  </td>
                </tr>
                {totalAdditionalDiscount > 0 && (
                  <tr>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-700"
                      } font-medium`}
                    >
                        {t("INVOICE.ADDITIONAL_DISCOUNTS")}
                      {item.additionalDiscount?.map((discount, idx) => (
                        <div key={idx} className='text-xs font-normal'>
                          • {discount.label}: {discount.amount}{" "}
                          {discount.type === "discount" ? "%" : "₹"}
                        </div>
                      ))}
                    </td>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-600"
                      }`}
                    >
                      -
                      {formatCurrency(
                        totalAdditionalDiscount,
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
                {totalAdditionalFee > 0 && (
                  <tr>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-700"
                      } font-medium`}
                    >
                           {t("INVOICE.ADDITIONAL_FEES")}
                      {item.additionalFee?.map((fee, idx) => (
                        <div key={idx} className='text-xs font-normal'>
                          • {fee.label}: {fee.amount}{" "}
                          {fee.type === "discount" ? "%" : "₹"}
                        </div>
                      ))}
                    </td>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-600"
                      }`}
                    >
                      +{formatCurrency(totalAdditionalFee, bussiness?.currency)}
                    </td>
                  </tr>
                )}
                {item.subscriptionDisc > 0 && (
                  <tr>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-700"
                      } font-medium`}
                    >
                      {t("INVOICE.SUBSCRIPTION_SAVINGS")}
                    </td>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-600"
                      }`}
                    >
                      -
                      {formatCurrency(
                        item.subscriptionDisc,
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
                <tr
                  className={`border-t-2 bg-[#f9f7f7] border-gray-300 font-bold ${
                    darkMode ? "text-black" : "text-gray-800"
                  }`}
                >
                  <td className='py-1 px-3 border border-gray-300'>
                    {t("COMMON.TOTAL_AMOUNT")}
                  </td>
                  <td className='py-1 px-3 border border-gray-300'>
                    {formatCurrency(
                      grandTotal -
                        totalAdditionalDiscount +
                        totalAdditionalFee -
                        (item.subscriptionDisc || 0),
                      bussiness?.currency
                    )}
                  </td>
                </tr>
                {(isAdvancePayment || item?.saleType === "advanceSale") && (
                  <tr>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-700"
                      } font-medium`}
                    >
                       {t("INVOICE.ADVANCE_AMOUNT")}
                    </td>
                    <td
                      className={`py-1 px-3 border ${
                        darkMode
                          ? "border-gray-300 text-gray-300"
                          : "border-gray-300 text-gray-600"
                      }`}
                    >
                      {formatCurrency(item.advancePayment, bussiness?.currency)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 text-gray-300"
                        : "border-gray-300 text-gray-700"
                    } font-medium`}
                  >
                    {t("NEWINVOICE.PAYMENT_MODE")}
                  </td>
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 text-gray-300"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    {item.paymentMode || "-"}
                  </td>
                </tr>
                <tr>
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 text-gray-300"
                        : "border-gray-300 text-gray-700"
                    } font-medium`}
                  >
                    {isAdvancePayment
                      ? "Remaining Balance"
                      : t("COMMON.TOTAL_VALUE")}
                  </td>
                  <td
                    className={`py-1 px-3 border ${
                      darkMode
                        ? "border-gray-300 text-gray-300"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    {isReturnInvoice
                      ? formatCurrency(
                          item.totalReturnAmount,
                          bussiness?.currency
                        )
                      : formatCurrency(
                          grandTotal -
                            totalAdditionalDiscount +
                            totalAdditionalFee -
                            (item.subscriptionDisc || 0) -
                            parseFloat(item.advancePayment || 0),
                          bussiness?.currency
                        )}
                  </td>
                </tr>
                {item.remainingTransactionAmount > 0 && (
                  <>
                    <tr>
                      <td
                        className={`py-1 px-3 border ${
                          darkMode
                            ? "border-gray-300 text-gray-300"
                            : "border-gray-300 text-gray-700"
                        } font-medium`}
                      >
                        {t("NEWINVOICE.RECEIVED")}
                      </td>
                      <td
                        className={`py-1 px-3 border ${
                          darkMode
                            ? "border-gray-300 text-gray-300"
                            : "border-gray-300 text-gray-600"
                        }`}
                      >
                        {formatCurrency(paidAmount, bussiness?.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className={`py-1 px-3 border ${
                          darkMode
                            ? "border-gray-300 text-gray-300"
                            : "border-gray-300 text-gray-700"
                        } font-bold`}
                      >
                         {t("INVOICE.DUE_BALANCE")}
                      </td>
                      <td
                        className={`py-1 px-3 border ${
                          darkMode
                            ? "border-gray-300 text-gray-300"
                            : "border-gray-300 text-gray-600"
                        } font-bold`}
                      >
                        {formatCurrency(remainingBalance, bussiness?.currency)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            <div className='mt-12 p-4 bg-gray-100 rounded-lg'>
              <div className='flex justify-center'>
                <div className='w-48 h-26 bg-white border-2 border-dashed border-gray-300 flex items-center justify-center'>
                  {bussiness?.signature ? (
                    <img
                      src={AWS_URL + bussiness.signature}
                      alt='Signature'
                      className='w-full h-full object-contain'
                      // crossOrigin='anonymous'
                    />
                  ) : (
                    <span className='text-gray-400 text-xs text-center'>
                         {t("INVOICE.SIGNATURE")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='text-center mt-4'>
              <p
                className={
                  darkMode
                    ? "text-gray-300 font-medium"
                    : "text-gray-700 font-medium"
                }
              >
                {t("NEWINVOICE.AUTHORIZED_SIGNATORY")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='grid grid-cols-3 gap-2 mt-6'>
        <div className='col-span-2'>
          <div className='border-t border-gray-300 pt-4'>
            <div className='mb-4'>
              <h3
                className={`text-lg font-semibold ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {t("NEWINVOICE.AMOUNT_IN_WORDS")}
              </h3>
              <p
                className={
                  darkMode
                    ? "text-gray-400 italic text-sm"
                    : "text-gray-600 italic text-sm"
                }
              >
                {amountInwords} Rupees only
              </p>
            </div>
            {item.notes && (
              <div className='mb-4'>
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                        {t("INVOICE.NOTES")}
                </h3>
                <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  {item.notes}
                </p>
              </div>
            )}
            <div className='flex justify-between items-end'>
              <div>
                <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  {t("NEWINVOICE.FOR_COMPANY")}: {bussiness.businessName}
                </p>
                <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  {item.authorizedPerson || ""}
                </p>
              </div>
            </div>
          </div>
          {bussiness?.aadharPhoto && (
            <div
              className='flex p-4 bg-gray-100 rounded-lg mt-8'
              style={{ width: "35%" }}
            >
              <img
                src={AWS_URL + bussiness?.aadharPhoto}
                alt='Payment QR Code'
                className='w-30 object-contain'
                // crossOrigin='anonymous'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GreenDoubleDevine;
